import { getBlueprintCalculations } from 'features/blueprints';
import { powerProfileModes } from 'greener-ui-library';
import { projectTypeIdToString } from 'pages/Blueprints/components/utils';
import { Blueprint } from 'types';
import { convertFromCents } from 'utils/currencyUtils';
import { BlueprintFormData } from '../types';
import { HybridReason, RentalSource } from './constants';

export const mapBlueprintToFormData = (blueprint: Blueprint): Partial<BlueprintFormData> => {
  const { hybrid, conventional } = getBlueprintCalculations(blueprint);
  const hybridReasonValues = new Set(Object.values(HybridReason));
  const allHybridReasons = blueprint.hybridReasons ?? [];
  const enumHybridReasons = allHybridReasons.filter((reason) => hybridReasonValues.has(reason));
  const customHybridReasons = allHybridReasons.filter((reason) => !hybridReasonValues.has(reason));

  if (!hybrid || !conventional) {
    return {};
  }

  return {
    blueprintName: blueprint.name,
    clientName: blueprint.clientName,
    applicationType: projectTypeIdToString(blueprint.projectTypeId),
    startDate: new Date(blueprint.tsStart),
    endDate: blueprint.tsEnd ? new Date(blueprint.tsEnd) : undefined,
    hybridReasons: enumHybridReasons,
    hybridReasonsCustom: customHybridReasons[0],
    partnerOrganisation: blueprint.partnerOrganisation ?? blueprint.organisation,
    partnerOrganisationId: (blueprint.partnerOrganisation ?? blueprint.organisation)?.organisationId ?? null,

    // Determine mode from powerProfileCreateMode from API, default to manual if not present
    powerProfileMode: blueprint.powerProfileCreateMode === 'file' ? powerProfileModes.file : powerProfileModes.manual,
    peakPowerKw: hybrid.loadProfile?.peakLoadKw,
    averagePowerKw: undefined,
    workingDays:
      hybrid.loadProfile?.days?.map((d) => {
        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
        return dayNames[d.dayOfWeek];
      }) || [],
    workingHoursStart: hybrid.loadProfile?.startHour
      ? `${hybrid.loadProfile.startHour.toString().padStart(2, '0')}:00`
      : '08:00',
    workingHoursEnd: hybrid.loadProfile?.endHour
      ? `${hybrid.loadProfile.endHour.toString().padStart(2, '0')}:00`
      : '15:00',
    // Step 2 values come from conventional calculation
    peaksPerDay: undefined,
    peaksDuration: undefined,
    restDayAveragePowerKw: undefined,
    gensetKva: conventional.powerSetup.generatorCapacityKva || '',
    gensetCount: conventional.powerSetup.generatorCount || '',
    mainsConnection:
      (hybrid.powerSetup.mainsConnectionMaxAmps ?? 0) > 0 || (hybrid.powerSetup.mainsConnectionMaxKw ?? 0) > 0,
    mainsConnectionCapacityUnit:
      (hybrid.powerSetup.mainsConnectionMaxAmps ?? 0) > 0
        ? 'amps'
        : (hybrid.powerSetup.mainsConnectionMaxKw ?? 0) > 0
          ? 'kw'
          : undefined,
    maxAmpsAvailable: hybrid.powerSetup.mainsConnectionMaxAmps ?? 0,
    maxKwAvailable: hybrid.powerSetup.mainsConnectionMaxKw ?? 0,
    kitePowerScenario: hybrid.powerSetup.kitePowerScenario ?? null,

    // Business Case
    currency: hybrid.currencyCode.toUpperCase(),
    gensetRentalSource: hybrid.powerSetup.generatorTypeId != null ? RentalSource.CATALOG : RentalSource.MANUAL,
    gensetType: hybrid.powerSetup.generatorTypeId != null ? hybrid.powerSetup.generatorTypeId.toString() : '',
    // Only set hybridGensetKva if rental source is MANUAL (no generatorTypeId)
    hybridGensetKva:
      hybrid.powerSetup.generatorTypeId == null ? (hybrid.powerSetup.generatorCapacityKva ?? undefined) : undefined,
    gensetRentalCount: hybrid.powerSetup.generatorCount!,
    gensetRentalCost: convertFromCents(hybrid.costAssumptions.gensetRentalCostCents),

    batteryRentalSource: hybrid.powerSetup.batteryTypeId != null ? RentalSource.CATALOG : RentalSource.MANUAL,
    batteryModel: hybrid.powerSetup.batteryTypeId != null ? hybrid.powerSetup.batteryTypeId.toString() : '',
    // Only set batterySize and converterPower if rental source is MANUAL (no batteryTypeId)
    batterySize: hybrid.powerSetup.batteryTypeId == null ? (hybrid.powerSetup.batterySizeKwh ?? undefined) : undefined,
    converterPower:
      hybrid.powerSetup.batteryTypeId == null ? (hybrid.powerSetup.batteryConverterPowerKw ?? undefined) : undefined,
    batteryRentalCount: hybrid.powerSetup.batteryCount || 1,
    batteryRentalCost: convertFromCents(hybrid.costAssumptions.batteryRentalCostCents || 0),

    hybridDieselPricePerLiter: convertFromCents(hybrid.costAssumptions.dieselPricePerLiterPriceCents),
    hybridElectricityPricePerKwh: hybrid.costAssumptions.energyPricePerKwhPriceCents
      ? convertFromCents(hybrid.costAssumptions.energyPricePerKwhPriceCents)
      : undefined,
    hybridAdditionalCostsPerWeek: hybrid.costAssumptions.additionalCostsCents
      ? convertFromCents(hybrid.costAssumptions.additionalCostsCents)
      : undefined,
    co2CostPerTon: hybrid.costAssumptions.co2CostPerTonCents
      ? convertFromCents(hybrid.costAssumptions.co2CostPerTonCents)
      : undefined,
    conventionalDieselPricePerLiter: convertFromCents(conventional.costAssumptions.dieselPricePerLiterPriceCents),
    conventionalGensetRentalCostPerWeek: convertFromCents(conventional.costAssumptions.gensetRentalCostCents),
    conventionalAdditionalCostsPerWeek: conventional.costAssumptions.additionalCostsCents
      ? convertFromCents(conventional.costAssumptions.additionalCostsCents)
      : undefined,
  };
};
